import React, { useRef, useState } from "react";
import { api, formatError, resolvePhotoUrl } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Trash2, ImageIcon } from "@/lib/icons";

const ACCEPT = "image/jpeg,image/jpg,image/png,image/webp";
const MAX_MB = 5;

/**
 * Simple site-logo uploader. Uploads directly to /admin/site/logo and
 * notifies the parent of the new URL (already saved server-side).
 */
export default function LogoUpload({ src, onChange, testidPrefix = "logo" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const doUpload = async (f) => {
    if (!f) return;
    if (!ACCEPT.split(",").includes(f.type.toLowerCase())) {
      toast.error("Only JPG, PNG or WEBP files are allowed");
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`Logo must be ${MAX_MB} MB or less`);
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/site/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Logo updated");
      onChange?.(data.logo_url);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const doDelete = async () => {
    if (!window.confirm("Remove the site logo?")) return;
    setBusy(true);
    try {
      await api.delete("/admin/site/logo");
      toast.success("Logo removed");
      onChange?.("");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  const resolved = resolvePhotoUrl(src);

  return (
    <div className="flex items-center gap-4">
      <div
        className="logo-upload-preview relative shrink-0 rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-br from-violet-800/40 to-magenta-700/30 flex items-center justify-center"
        style={{ width: 120, height: 120 }}
        data-testid={`${testidPrefix}-preview`}
      >
        {resolved ? (
          <img src={resolved} alt="Site logo" className="w-full h-full object-contain p-2" />
        ) : (
          <ImageIcon size={44} className="text-fuchsia-300/50" />
        )}
        {busy && (
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center text-white text-xs">
            Working…
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2">
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => doUpload(e.target.files?.[0])}
          data-testid={`${testidPrefix}-input`}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid={`${testidPrefix}-upload-btn`}
          className="btn-ghost inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold text-white disabled:opacity-60"
        >
          <Upload size={12} /> {resolved ? "Change logo" : "Upload logo"}
        </button>
        {resolved && (
          <button
            type="button"
            onClick={doDelete}
            disabled={busy}
            data-testid={`${testidPrefix}-delete-btn`}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold text-red-300 border border-red-500/25 hover:bg-red-500/10 disabled:opacity-60"
          >
            <Trash2 size={12} /> Remove
          </button>
        )}
        <div className="text-[10px] text-white/40">JPG / PNG / WEBP · ≤ {MAX_MB} MB</div>
      </div>
    </div>
  );
}
